import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "contributorList", "selectedSection", "selectedList", "saveButton", "modal"]
  static values = { ideaId: Number }

  connect() {
    this.selectedContributors = []
    this.allContributors = []
    
    // Listen for modal events
    this.modalTarget.addEventListener('shown.bs.modal', () => {
      this.loadContributors()
    })
    
    this.modalTarget.addEventListener('hidden.bs.modal', () => {
      this.resetModal()
    })
  }

  async loadContributors() {
    try {
      const response = await fetch(`/contributors/ideas/${this.ideaIdValue}/assign_contributors.json`, {
        method: 'GET',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      
      this.allContributors = data.contributors
      // Pre-populate with currently assigned contributors - ensure integers
      this.selectedContributors = (data.assigned_contributors || []).map(id => parseInt(id))
      
      this.renderContributors()
      this.updateSelectedDisplay()
      this.updateSaveButton()
    } catch (error) {
      console.error('Error loading contributors:', error)
      this.contributorListTarget.innerHTML = '<div class="alert alert-danger">Error loading contributors. Please try again.</div>'
    }
  }

  renderContributors() {
    const searchTerm = this.searchInputTarget.value.toLowerCase()
    
    this.contributorListTarget.innerHTML = this.allContributors
      .filter(contributor => {
        const fullName = `${contributor.fname} ${contributor.lname}`.toLowerCase()
        const role = (contributor.role || '').toLowerCase()
        return fullName.includes(searchTerm) || role.includes(searchTerm)
      })
      .map(contributor => this.contributorHtml(contributor))
      .join('')
    
    // Add click listeners
    this.contributorListTarget.querySelectorAll('.contributor-item').forEach(item => {
      item.addEventListener('click', (e) => {
        const contributorId = parseInt(e.currentTarget.dataset.contributorId)
        this.toggleContributor(contributorId)
      })
    })
  }

  contributorHtml(contributor) {
    const isSelected = this.selectedContributors.includes(contributor.id)
    const selectedClass = isSelected ? 'selected' : ''
    
    // Create avatar HTML with fallback to initials
    const avatarHtml = contributor.avatar_url 
      ? `<img src="${contributor.avatar_url}" alt="${contributor.fname} ${contributor.lname}" class="contributor-avatar rounded-circle" width="40" height="40" style="object-fit: cover;">`
      : `<div class="contributor-avatar-fallback rounded-circle d-flex align-items-center justify-content-center" style="width: 40px; height: 40px; background-color: #e9ecef; color: #6c757d; font-weight: 600; font-size: 14px;">
           ${contributor.fname?.charAt(0) || ''}${contributor.lname?.charAt(0) || ''}
         </div>`;
    
    return `
      <div class="contributor-item ${selectedClass}" data-contributor-id="${contributor.id}">
        <div class="d-flex align-items-center">
          <div class="me-3">
            ${avatarHtml}
          </div>
          <div class="flex-grow-1">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <div class="contributor-name fw-bold">
              ${contributor.fname} ${contributor.lname}
              <span class="badge bg-secondary ms-2">${contributor.role ? contributor.role.charAt(0).toUpperCase() + contributor.role.slice(1) : 'Contributor'}</span>
            </div>
            <small class="text-muted">
              Active Ideas: ${contributor.ideas_in_progress_count || 0} | 
              Pending Pitches: ${contributor.idea_pitches_count || 0}
            </small>
          </div>
          <div>
            ${isSelected ? '<i class="bi bi-check-circle-fill text-success"></i>' : '<i class="bi bi-circle text-muted"></i>'}
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }

  toggleContributor(contributorId) {
    const index = this.selectedContributors.indexOf(contributorId)
    
    if (index === -1) {
      // Add contributor
      this.selectedContributors.push(contributorId)
    } else {
      // Remove contributor
      this.selectedContributors.splice(index, 1)
    }
    
    this.updateSelectedDisplay()
    this.renderContributors() // Re-render to update selection state
    this.updateSaveButton()
  }

  updateSelectedDisplay() {
    if (this.selectedContributors.length === 0) {
      this.selectedSectionTarget.classList.add('d-none')
      return
    }
    
    this.selectedSectionTarget.classList.remove('d-none')
    
    this.selectedListTarget.innerHTML = this.selectedContributors
      .map((contributorId, index) => {
        const contributor = this.allContributors.find(c => c.id === contributorId)
        if (!contributor) return '' // Skip if contributor not found
        
        // Create avatar HTML with fallback to initials (same as contributorHtml)
        const avatarHtml = contributor.avatar_url 
          ? `<img src="${contributor.avatar_url}" alt="${contributor.fname} ${contributor.lname}" class="contributor-avatar rounded-circle" width="32" height="32" style="object-fit: cover;">`
          : `<div class="contributor-avatar-fallback rounded-circle d-flex align-items-center justify-content-center" style="width: 32px; height: 32px; background-color: #e9ecef; color: #6c757d; font-weight: 600; font-size: 12px;">
               ${contributor.fname?.charAt(0) || ''}${contributor.lname?.charAt(0) || ''}
             </div>`;
        
        return `
          <div class="selected-contributor d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <span class="priority-badge me-3">${index + 1}</span>
              <div class="me-3">
                ${avatarHtml}
              </div>
              <div>
                <div class="fw-bold">${contributor.fname} ${contributor.lname}</div>
                <small class="text-muted">${contributor.role ? contributor.role.charAt(0).toUpperCase() + contributor.role.slice(1) : 'Contributor'}</small>
              </div>
            </div>
            <a href="#" class="text-danger small" 
               data-action="click->contributor-assignment#removeContributor" data-contributor-id="${contributorId}">
              remove
            </a>
          </div>
        `
      })
      .filter(html => html !== '') // Remove empty entries
      .join('')
  }

  removeContributor(event) {
    event.preventDefault()
    event.stopPropagation()
    const contributorId = parseInt(event.currentTarget.dataset.contributorId)
    this.toggleContributor(contributorId)
  }

  updateSaveButton() {
    this.saveButtonTarget.disabled = this.selectedContributors.length === 0
  }

  searchContributors() {
    this.renderContributors()
  }

  showModalError(message) {
    // Remove any existing error alerts
    const existingAlerts = this.modalTarget.querySelectorAll('.alert-danger')
    existingAlerts.forEach(alert => alert.remove())
    
    // Create error alert
    const errorAlert = document.createElement('div')
    errorAlert.className = 'alert alert-danger alert-dismissible fade show'
    errorAlert.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `
    
    // Insert at the top of modal body
    const modalBody = this.modalTarget.querySelector('.modal-body')
    modalBody.insertBefore(errorAlert, modalBody.firstChild)
  }

  resetModal() {
    this.selectedContributors = []
    this.searchInputTarget.value = ''
    this.selectedSectionTarget.classList.add('d-none')
    this.updateSaveButton()
    
    // Remove any error alerts
    const errorAlerts = this.modalTarget.querySelectorAll('.alert-danger')
    errorAlerts.forEach(alert => alert.remove())
  }

  // Handle form submission
  async submitForm(event) {
    event.preventDefault()
    
    const form = document.getElementById('assignContributorsForm')
    const formData = new FormData()
    
    this.selectedContributors.forEach(contributorId => {
      formData.append('contributor_ids[]', contributorId)
    })
    
    try {
      const response = await fetch(form.action, {
        method: 'PATCH',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      
      // Handle both explicit success flag and successful HTTP response
      if (data.success !== false && response.ok) {
        // Update the assignment status in the sidebar
        const assignmentContainer = document.querySelector('[data-assignment-status]')
        if (assignmentContainer && data.assignment_html) {
          assignmentContainer.innerHTML = data.assignment_html
        }
        
        // Update the status module in the sidebar
        const statusModule = document.querySelector('[data-status-module]')
        if (statusModule && data.status_html) {
          statusModule.innerHTML = data.status_html
        }
        
        // Close modal using button click instead of bootstrap API
        const closeButton = this.modalTarget.querySelector('[data-bs-dismiss="modal"]')
        if (closeButton) {
          closeButton.click()
        }
        
        // Reset modal state
        this.resetModal()
        
      } else {
        // Show error in modal instead of alert
        this.showModalError(data.message || 'An error occurred while assigning contributors')
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      this.showModalError('An error occurred while assigning contributors. Please try again.')
    }
  }
}